import React, { FC, useState, useEffect } from 'react'
import isEqual from 'lodash/isEqual'
import { IAutocompleteOption } from 'transfix-ui/components/Autocomplete'
import { Avatar } from 'transfix-ui/components/Avatar'
import { Button } from 'transfix-ui/components/Button'
import { Box, BoxProps } from 'transfix-ui/core'
import TagAutosuggest from 'apps/marketplace/components/TagAutosuggest'
import { RemoveIcon } from 'transfix-icons'

interface ITagSubscribersProps {
  value?: IAutocompleteOption[]
  onUpdateSubscribers: (subscribers: Partial<IAutocompleteOption>[]) => void
  OuterBoxProps?: BoxProps
  readOnly?: boolean
}

/**
 * Tag subscribers to follow issue status + changes
 */
const TagSubscribers: FC<ITagSubscribersProps> = ({
  value = [],
  onUpdateSubscribers,
  OuterBoxProps = {},
  readOnly = false,
}) => {
  const defaultSelected = value.length > 0 ? value : []

  const [selected, setSelected] = useState<Partial<IAutocompleteOption> | undefined>(undefined)
  const [tags, setTags] = useState<Partial<IAutocompleteOption>[]>(defaultSelected)

  // Update when outside value of control also changed
  // So list is updated inside of the state component too
  useEffect(() => {
    // Avoid circular loop and checks if value need to be mapped
    // If value don't needs to be mapped then it the same one as before
    if (!isEqual(value, tags)) {
      setTags(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const changeSelectHandler = (option?: Partial<IAutocompleteOption>) => setSelected(option)

  const onClickAddHandler = () => {
    if (selected) {
      // If selected tag is already in tags list, do not update list
      if (tags.find(tag => tag.id === selected.id)) {
        return
      }

      const subscribers = [...tags, selected]

      onUpdateSubscribers(subscribers)
    }

    setSelected(undefined)
  }

  const onClickRemoveHandler = (ev: React.MouseEvent) => {
    ev.preventDefault()
    // eslint-disable-next-line no-unused-expressions
    ev.currentTarget.parentElement?.parentElement?.classList.add('fadeOut')

    const subscribers = tags.filter(tag => String(ev.currentTarget.id) !== String(tag.id))

    onUpdateSubscribers(subscribers)
  }

  return (
    <div>
      {!readOnly && (
        <Box display='flex' {...OuterBoxProps}>
          <Box mr={1} width='100%'>
            <TagAutosuggest
              value={selected}
              onChange={changeSelectHandler}
              placeholder='Search for user or group'
              withAvatar
            />
          </Box>
          <Button disabled={!selected} color='primary' onClick={onClickAddHandler} className='w-40'>
            Add
          </Button>
        </Box>
      )}
      <Box mt={3}>
        {tags.map(tag => (
          <div key={tag.id} className='fadeIn'>
            <Box mt={3} display='flex' alignItems='center' justifyContent='space-between'>
              <Box display='flex' alignItems='center'>
                <Avatar size='xsmall' name={tag.text} />
                <Box ml={2}>{tag.text}</Box>
              </Box>
              <div>
                {!readOnly && (
                  <Button
                    startIcon={<RemoveIcon />}
                    type='submit'
                    id={tag.id}
                    onClick={onClickRemoveHandler}>
                    Remove
                  </Button>
                )}
              </div>
            </Box>
          </div>
        ))}
      </Box>
    </div>
  )
}

export default TagSubscribers
